import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Stack, Typography, Button, IconButton, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Chip, Divider, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const API_BASE = 'https://portal.peaksoftcon.com:8081/api';

const TR_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const MONTHLY_STATUS_FIELDS = [
  { key: 'dgs_yapildi',         label: 'DGS Yapıldı' },
  { key: 'puantaj_yapildi',     label: 'Puantaj Yapıldı' },
  { key: 'mali_veri_istendi',   label: 'Mali Veri İstendi' },
  { key: 'raporlar_hazirlandi', label: 'Raporlar Hazırlandı' },
  { key: 'yuklendi',            label: 'Yüklendi' },
  { key: 'imzalandi',           label: 'İmzalandı' },
];
const YEARLY_STATUS_FIELDS = [
  { key: 'rapor_cekildi',       label: 'Rapor Çekildi' },
  { key: 'firmaya_iletildi',    label: 'Firmaya İletildi' },
  { key: 'veri_duzeltmesi_var', label: 'Veri Düzeltmesi Var' },
  { key: 'imza_icin_iletildi',  label: 'İmza için İletildi' },
  { key: 'teslim_edildi',       label: 'Teslim Edildi' },
];
const YEARLY_REPORT_MONTH = 5;

const FILE_TITLES = [
  'Aylık Muafiyet Raporu',
  'DGS Formu',
  'Rapor',
  'SGK Fişleri',
  'Muhtasar',
];

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function ClientCompanyTracking() {
  const info = (() => {
    try { return JSON.parse(localStorage.getItem('companyInfo') || 'null'); }
    catch { return null; }
  })();
  const companyID = info?.ID;
  const companyName = info?.name || info?.fullname || 'Firmanız';

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [row, setRow] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState(FILE_TITLES[0]);
  const [uploading, setUploading] = useState(false);

  const loadRow = useCallback(async () => {
    if (!companyID) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/companyTracking`, {
        params: { year, month, companyID },
      });
      const r = (data && data[0]) || null;
      setRow(r);
      if (r) {
        const { data: fls } = await axios.get(`${API_BASE}/companyTracking/${r.id}/files`);
        setFiles(fls || []);
      } else {
        setFiles([]);
      }
    } catch (err) {
      console.error('client tracking load error:', err);
    } finally {
      setLoading(false);
    }
  }, [companyID, year, month]);

  useEffect(() => { loadRow(); }, [loadRow]);

  const submitUpload = async () => {
    if (!uploadFile || !row) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('baslik', uploadTitle);
      fd.append('companyID', String(companyID));
      await axios.post(
        `${API_BASE}/companyTracking/${row.id}/files`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setUploadOpen(false);
      setUploadFile(null);
      await loadRow();
    } catch (err) {
      alert('Yükleme hatası: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`"${f.file_name}" silinsin mi?`)) return;
    try {
      await axios.delete(`${API_BASE}/companyTracking/files/${f.id}`, {
        params: { companyID },
      });
      await loadRow();
    } catch (err) {
      alert('Silme hatası: ' + (err.response?.data?.error || err.message));
    }
  };

  const downloadUrl = (f) =>
    `${API_BASE}/companyTracking/files/${f.id}/download`;

  const yearOptions = [];
  const cy = now.getFullYear();
  for (let y = cy - 1; y <= cy + 1; y++) yearOptions.push(y);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        mb={2}
      >
        <Typography variant="h4" sx={{ flex: { sm: 1 } }}>Firma Takibi</Typography>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="cl-year">Yıl</InputLabel>
            <Select
              labelId="cl-year"
              label="Yıl"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="cl-month">Ay</InputLabel>
            <Select
              labelId="cl-month"
              label="Ay"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {TR_MONTHS.map((m, i) => <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {loading && (
        <Stack direction="row" justifyContent="center" my={4}>
          <CircularProgress />
        </Stack>
      )}

      {!loading && !row && (
        <Alert severity="info">Bu dönem için kayıt bulunamadı.</Alert>
      )}

      {!loading && row && (
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              mb={2}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {row.company_name || companyName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {TR_MONTHS[month - 1]} {year}
                </Typography>
              </Box>
              <Chip
                size="small"
                color="default"
                icon={<LockOutlinedIcon />}
                label="Durumlar salt-okunur"
                variant="outlined"
              />
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* ── Durum (read-only) ───────────────────────────── */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
              Aylık Durum
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                mb: month === YEARLY_REPORT_MONTH ? 3 : 1,
              }}
            >
              {MONTHLY_STATUS_FIELDS.map((f) => (
                <StatusRow key={f.key} label={f.label} done={!!row[f.key]} />
              ))}
            </Box>

            {month === YEARLY_REPORT_MONTH && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1.5, mt: 1, fontWeight: 700 }}>
                  Yıllık Rapor Durumu (Mayıs)
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)',
                    },
                    mb: 1,
                  }}
                >
                  {YEARLY_STATUS_FIELDS.map((f) => (
                    <StatusRow key={f.key} label={f.label} done={!!row[f.key]} />
                  ))}
                </Box>
              </>
            )}

            {row.notlar && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                  Notlar (Admin)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {row.notlar}
                </Typography>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* ── Dosyalar ────────────────────────────────────── */}
            <Stack direction="row" alignItems="center" mb={1.5}>
              <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
                Dosyalar
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadOpen(true)}
              >
                Yükle
              </Button>
            </Stack>

            {files.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                Bu dönem için henüz dosya yok.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {files.map((f) => {
                  const mine = f.uploaded_by_company_id === companyID;
                  return (
                    <Paper key={f.id} variant="outlined" sx={{ p: 1.25 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, wordBreak: 'break-all' }}
                          >
                            {f.file_name}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
                            <Chip size="small" variant="outlined" label={f.baslik} />
                            {mine ? (
                              <Chip size="small" color="primary" label="Sen yükledin" />
                            ) : (
                              <Chip size="small" color="default" label="Admin yükledi" variant="outlined" />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {fmtSize(f.size_bytes)}
                            </Typography>
                          </Stack>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            color="primary"
                            component="a"
                            href={downloadUrl(f)}
                            download={f.file_name}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          {mine && (
                            <IconButton size="small" color="error" onClick={() => deleteFile(f)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dosya Yükle</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel id="upload-baslik">Başlık</InputLabel>
            <Select
              labelId="upload-baslik"
              label="Başlık"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
            >
              {FILE_TITLES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            type="file"
            margin="dense"
            fullWidth
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
          />
          {uploadFile && (
            <Typography variant="caption" color="text.secondary">
              {uploadFile.name} — {fmtSize(uploadFile.size)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>İptal</Button>
          <Button
            variant="contained"
            onClick={submitUpload}
            disabled={uploading || !uploadFile}
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {uploading ? 'Yükleniyor...' : 'Yükle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function StatusRow({ label, done }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        p: 1,
        borderRadius: 1,
        bgcolor: done ? 'success.light' : 'action.hover',
        color: done ? 'success.contrastText' : 'text.secondary',
        opacity: done ? 1 : 0.85,
      }}
    >
      {done
        ? <CheckCircleIcon fontSize="small" sx={{ color: 'success.dark' }} />
        : <RadioButtonUncheckedIcon fontSize="small" />
      }
      <Typography
        variant="body2"
        sx={{
          fontWeight: done ? 700 : 500,
          color: done ? 'success.dark' : 'text.primary',
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}
